import { displayReturn } from "../Matches/matchNavigation";
import CancelledMatchDetail from "../Matches/CancelledMatchDetail";
import RelatedMatchSelector from "../Matches/RelatedMatchSelector";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { fetchUserAttributes, getCurrentUser } from "aws-amplify/auth";
import {
  getMatch,
  getMatchRegistrationSummary,
  getMyMatchRegistration,
  getMatchSetupDetail,
  getStageSetupDetail,
  getStageScoreDetail,
  listCompetitionEntries,
  listCompetitionResults,
  listMatchLeaderboards,
  listStageLeaderboards,
} from "../../lib/leaderboardApi";
import MatchLocationMap from "../Matches/MatchLocationMap";
import UpcomingMatchDetail from "../Matches/UpcomingMatchDetail";
import { isUpcomingDetail } from "../Matches/upcomingMatch";
import useMatchPageRefresh from "../Matches/useMatchPageRefresh";
import InProgressMatchDetail from "../Matches/InProgressMatchDetail";
import CompletedMatchDetail from "../Matches/CompletedMatchDetail";
import { completedArcSections, isCompletedDetail } from "../Matches/completedResults";
import { arcLiveSections, isInProgressDetail } from "../Matches/liveMatch";
import DetailOverlay from "./DetailOverlay";
import { MatchSetupRenderer, StageSetupRenderer } from "./JsonRenderers";
import {
  DEFAULT_LEADERBOARD_BANNER_SRC,
  preloadLeaderboardBanner,
} from "./leaderboardBannerPreload";
import { CompetitionErrorState } from "../Competition/CompetitionRoutePanels";
import { formatCalendarDate, formatCalendarDateTime } from "../../utils/date";
import "./Leaderboard.css";

function formatNumber(value) {
  return Number.isFinite(value) ? value : 0;
}

function formatTime(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num.toFixed(2) : "0.00";
}

function formatPercent(value) {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  return Number.isFinite(num) ? `${num.toFixed(2)}%` : "-";
}

function isLocalDisplayStressMode() {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname;
  if (hostname !== "localhost" && hostname !== "127.0.0.1") return false;
  // Local-only visual QA fixture for checking display containment with extreme names.
  return new URLSearchParams(window.location.search).get("stress") === "long-names";
}

const DISPLAY_STRESS_ROWS = [
  {
    competitorName: "Alexandria Cassandra Montgomery-Winters-Santiago",
    teamName: "Range Pocket Precision Performance Collective North America",
    totalScore: 19.07,
    scoreDisplay: "19.07",
    performanceDisplay: "100.00%",
    isNraAmbassador: true,
  },
  {
    competitorName: "Christopher Johnathan Van Der Meer Richardson",
    teamName: "Very Long Sponsored Team Name With Multiple Divisions",
    totalScore: 20.72,
    scoreDisplay: "20.72",
    performanceDisplay: "92.04%",
  },
  {
    competitorName: "Mikaela-Jean O'Connor-Livingston The Third",
    teamName: "International ARC Traveling Exhibition Team",
    totalScore: 22.49,
    scoreDisplay: "22.49",
    performanceDisplay: "84.79%",
  },
  {
    competitorName: "NoSpacesUltraLongCompetitorIdentifierForOverflowTesting",
    teamName: "NoSpacesUltraLongTeamIdentifierForOverflowTesting",
    totalScore: 23.55,
    scoreDisplay: "23.55",
    performanceDisplay: "80.98%",
  },
];

function buildDisplayStressMatch(match) {
  return {
    ...(match || {}),
    matchName: "NRA ARC Invitational Championship With An Intentionally Long Tournament Display Name",
    location: "George R. Brown Convention Center, Hall A, Houston, Texas - Finals Bay 12",
  };
}

function buildDisplayStressRows(rows = []) {
  const sourceRows = rows.length > 0 ? rows : DISPLAY_STRESS_ROWS;
  return sourceRows.map((row, index) => ({
    ...row,
    ...(DISPLAY_STRESS_ROWS[index % DISPLAY_STRESS_ROWS.length] || {}),
  }));
}

function normalizeDisplayRowCount(value, fallback = 20) {
  if (String(value || "").trim().toLowerCase() === "all") {
    return "all";
  }
  const numericValue = Number.parseInt(String(value || ""), 10);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
}

function getDisplayRowsParam(searchParams) {
  return searchParams.get("rows") || searchParams.get("top");
}

function matchDisplayPath(matchId, rows) {
  const path = `/matches/arc/${encodeURIComponent(matchId)}/display`;
  return rows === "all" ? `${path}?rows=all` : path;
}

function formatStatus(value) {
  return String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/^./, (char) => char.toUpperCase());
}

function formatDateTime(value) {
  return formatCalendarDateTime(value);
}

function formatDate(value) {
  if (!value) {
    return "-";
  }

  return formatCalendarDate(value, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function isResultsPhaseStatus(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return ["in_progress", "completed", "final", "official", "closed"].includes(normalized);
}

function normalizeMatchSquads(match) {
  const squads = Array.isArray(match?.squads) ? match.squads : [];
  return squads
    .map((squad) => {
      const squadId = String(squad?.id || squad?.squadId || "").trim();
      const rawNumber = squad?.number ?? squad?.squadNumber;
      const squadNumber = Number.isInteger(rawNumber)
        ? rawNumber
        : Number.parseInt(String(rawNumber || "").trim(), 10);
      const squadScheduledAt = String(
        squad?.matchTime || squad?.scheduledAt || squad?.squadScheduledAt || "",
      ).trim();

      if (!squadId && !Number.isInteger(squadNumber)) {
        return null;
      }

      return {
        squadId: squadId || "",
        label: String(squad?.label || "").trim(),
        squadNumber: Number.isInteger(squadNumber) ? squadNumber : null,
        squadScheduledAt: squadScheduledAt || "",
        maxCompetitorCount: Number.isFinite(Number(squad?.maxCompetitorCount))
          ? Number(squad.maxCompetitorCount)
          : null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const av = Number.isInteger(a?.squadNumber)
        ? a.squadNumber
        : Number.POSITIVE_INFINITY;
      const bv = Number.isInteger(b?.squadNumber)
        ? b.squadNumber
        : Number.POSITIVE_INFINITY;
      if (av !== bv) {
        return av - bv;
      }
      return String(a?.squadId || "").localeCompare(String(b?.squadId || ""));
    });
}

function squadChoiceValue(squad) {
  if (!squad) {
    return "";
  }
  if (String(squad.squadId || "").trim()) {
    return `id:${String(squad.squadId).trim()}`;
  }
  if (Number.isInteger(squad.squadNumber)) {
    return `number:${squad.squadNumber}`;
  }
  return "";
}

function findSquadByChoice(squads, choice) {
  const target = String(choice || "").trim();
  if (!target) {
    return null;
  }
  return squads.find((squad) => squadChoiceValue(squad) === target) || null;
}

function formatSquadLabel(squad) {
  if (!squad) {
    return "No squad assigned";
  }

  const parts = [];
  if (squad.label) {
    parts.push(squad.label);
  } else if (Number.isInteger(squad?.squadNumber)) {
    parts.push(`Squad ${squad.squadNumber}`);
  } else if (String(squad?.squadId || "").trim()) {
    parts.push(`Squad ${String(squad.squadId).trim()}`);
  } else {
    parts.push("Squad");
  }

  if (String(squad?.squadScheduledAt || "").trim()) {
    parts.push(formatDateTime(squad.squadScheduledAt));
  }

  return parts.join(" • ");
}

function buildSquadPayload(squad, { clearWhenMissing = false } = {}) {
  if (!squad) {
    return clearWhenMissing
      ? { squadId: null, squadNumber: null, squadScheduledAt: null }
      : {};
  }

  return {
    ...(String(squad.squadId || "").trim()
      ? { squadId: String(squad.squadId).trim() }
      : {}),
    ...(Number.isInteger(squad.squadNumber)
      ? { squadNumber: squad.squadNumber }
      : {}),
    ...(String(squad.squadScheduledAt || "").trim()
      ? { squadScheduledAt: String(squad.squadScheduledAt).trim() }
      : {}),
  };
}

function matchLocationLabel(match) {
  const primary = String(match?.location || "").trim();
  if (primary) {
    return primary;
  }

  const city = String(match?.city || "").trim();
  const state = String(match?.state || "").trim();
  const range = String(match?.rangeName || "").trim();

  if (city && state) {
    return `${city}, ${state}`;
  }
  if (range && state) {
    return `${range}, ${state}`;
  }
  if (range) {
    return range;
  }
  if (state) {
    return state;
  }

  return "Location not set";
}

function matchAddressLabel(match) {
  const rangeName = String(match?.rangeName || "").trim();
  const rangeAddress = String(match?.rangeAddress || "").trim();
  const city = String(match?.city || "").trim();
  const state = String(match?.state || "").trim();

  const parts = [];
  if (rangeName) parts.push(rangeName);
  if (rangeAddress) parts.push(rangeAddress);
  const addressLower = rangeAddress.toLowerCase();
  const alreadyHasCity = city && addressLower.includes(city.toLowerCase());
  const alreadyHasState = state && addressLower.includes(state.toLowerCase());
  if (!rangeAddress && city && state) {
    parts.push(`${city}, ${state}`);
  } else if (!rangeAddress && city) {
    parts.push(city);
  } else if (!rangeAddress && state) {
    parts.push(state);
  } else if (rangeAddress && city && state && !alreadyHasCity && !alreadyHasState) {
    parts.push(`${city}, ${state}`);
  }

  return parts.filter(Boolean).join(" • ") || "Address not set";
}

function buildArcRegistrationFacts(summary, squads) {
  const legacyActiveRegistrationCount = Number(summary?.activeRegistrationCount);
  const canonicalActiveParticipantCount = Number(summary?.activeParticipants);
  const canonicalActiveRegistrationCount =
    Number(summary?.pending || 0) + Number(summary?.confirmed || 0);
  const activeRegistrationCount = Number.isFinite(legacyActiveRegistrationCount)
    ? legacyActiveRegistrationCount
    : Number.isFinite(canonicalActiveParticipantCount) && canonicalActiveParticipantCount > 0
      ? canonicalActiveParticipantCount
      : canonicalActiveRegistrationCount;
  const summarySquads = Array.isArray(summary?.squads) ? summary.squads : [];
  const visibleSquads = summarySquads.length > 0 ? summarySquads : squads;
  const capacitySource = summarySquads.length > 0 ? summarySquads : visibleSquads;
  const capacityValues = capacitySource
    .map((squad) => Number(squad?.maxCompetitorCount))
    .filter(Number.isFinite);
  const remainingValues = summarySquads
    .map((squad) => Number(squad?.remainingCapacity))
    .filter(Number.isFinite);
  const capacity = capacityValues.length > 0
    ? capacityValues.reduce((sum, value) => sum + value, 0)
    : null;

  return {
    registered: activeRegistrationCount,
    squadCount: Array.isArray(visibleSquads) ? visibleSquads.length : 0,
    capacity,
    remaining: remainingValues.length > 0
      ? remainingValues.reduce((sum, value) => sum + value, 0)
      : Number.isFinite(capacity)
        ? Math.max(capacity - activeRegistrationCount, 0)
        : null,
    registrationOpen: summary?.registrationOpen === true,
  };
}

function asTimeValue(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : Number.POSITIVE_INFINITY;
}

function sortByTimeAsc(rows, field) {
  return [...rows].sort((a, b) => {
    const av = asTimeValue(a?.[field]);
    const bv = asTimeValue(b?.[field]);
    if (av !== bv) {
      return av - bv;
    }
    return String(a?.competitorName || a?.entryId || "").localeCompare(
      String(b?.competitorName || b?.entryId || ""),
    );
  });
}

function normalizeCanonicalArcResultRows(rows = [], { squadId = null, squadNumber = null } = {}) {
  return sortByTimeAsc(
    (Array.isArray(rows) ? rows : [])
      .filter((row) => String(row?.discipline || "").toLowerCase() === "arc")
      .filter((row) => {
        if (squadId) return String(row.squadId || row.metadata?.squadId || "") === squadId;
        if (squadNumber == null) return true;
        const number = row.squadNumber ?? row.metadata?.squadNumber;
        return number != null && Number(number) === squadNumber;
      })
      .map((row) => ({
        ...row,
        sk: row.resultId,
        competitorId: row.participantId,
        entryId: row.entryId || row.participantId,
        competitorName: row.displayName || "Competitor",
        divisionId: row.metadata?.division || row.score?.division || row.divisionId || "",
        totalScore: Number(row.classificationScore ?? row.score?.aggregate ?? 0),
        scoreDisplay: row.nativeScore || String(row.classificationScore ?? "-"),
        reviewStatus: ["official", "confirmed", "final"].includes(String(row.status || "").toLowerCase()) ? "confirmed" : "inProgress",
        canonicalAggregate: ["official", "confirmed", "final"].includes(String(row.status || "").toLowerCase()),
        outcomeStatus: row.outcomeStatus || "active",
        outcomeReasonCode: row.outcomeReasonCode || "",
        outcomeReasonLabel: row.outcomeReasonLabel || "",
        outcomeNotes: row.outcomeNotes || "",
        outcomeUpdatedAt: row.outcomeUpdatedAt || "",
        isRankEligible: row.isRankEligible !== false,
      })),
    "totalScore",
  );
}

function buildStageItems(match, stageRows) {
  const fromMatch = Array.isArray(match?.stages) ? match.stages : [];
  const map = new Map();

  fromMatch.forEach((stage) => {
    const stageId = stage?.stageId || stage?.id;
    if (!stageId) {
      return;
    }

    map.set(stageId, {
      stageId,
      name: stage?.name || stageId,
      timeEntryMode: stage?.timeEntryMode,
      maxStageTimeSeconds: stage?.maxStageTimeSeconds,
    });
  });

  stageRows.forEach((row) => {
    const stageId = row?.stageId;
    if (!stageId || map.has(stageId)) {
      return;
    }

    map.set(stageId, {
      stageId,
      name: stageId,
      timeEntryMode: row?.timeEntryMode,
      maxStageTimeSeconds: row?.maxStageTimeSeconds,
    });
  });

  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function isLeaderboardRowComplete(row) {
  return String(row?.reviewStatus || "").trim().toLowerCase() === "confirmed";
}

function isRankEligibleRow(row) {
  const outcomeStatus = String(row?.outcomeStatus || "active").trim().toLowerCase();
  if (row?.isRankEligible === false) {
    return false;
  }
  return !["dq", "dnf", "withdrawn"].includes(outcomeStatus);
}

function formatAnalysisScoreStatus(row) {
  return [isLeaderboardRowComplete(row) ? "Confirmed" : "Unconfirmed", formatOutcomeStatus(row)]
    .filter(Boolean).join(" · ");
}

function isAnalysisBaselineEligible(row) {
  return isLeaderboardRowComplete(row) && isRankEligibleRow(row)
    && row.stageScore != null && String(row.stageScore).trim() !== ""
    && Number.isFinite(Number(row.stageScore)) && Number(row.stageScore) >= 0;
}

function formatOutcomeStatus(row) {
  const outcomeStatus = String(row?.outcomeStatus || "").trim().toLowerCase();
  const statusLabel = outcomeStatus === "dq"
    ? "DQ"
    : outcomeStatus === "dnf"
      ? "DNF"
      : outcomeStatus === "withdrawn"
        ? "Withdrawn"
        : "";
  if (!statusLabel) return "";
  const reason = String(row?.outcomeReasonLabel || row?.metadata?.outcomeReasonLabel || "").trim();
  return [statusLabel, reason.toLowerCase() === statusLabel.toLowerCase() ? "" : reason].filter(Boolean).join(" - ");
}

function stageDisplayName(stage, index) {
  const name = String(stage?.name || "").trim();
  const looksLikeUuid =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      name,
    );
  if (name && !looksLikeUuid) {
    return name;
  }
  return `Stage ${index + 1}`;
}

function formatStageTimingMode(stage) {
  if (!stage?.timeEntryMode) {
    return "Timing mode unavailable";
  }

  if (["enteredByRO", "RO Enters Raw Time"].includes(stage.timeEntryMode)) {
    const cap = Number(stage?.maxStageTimeSeconds);
    if (Number.isFinite(cap) && cap > 0) {
      return `Raw Time (RO Entered, cap ${cap}s)`;
    }
    return "Raw Time (RO Entered)";
  }

  return ["fixedZero", "Fixed Zero (Limited)"].includes(stage.timeEntryMode)
    ? "Limited (Base 0s)"
    : "Timing mode unavailable";
}

function leaderboardRowKey(row) {
  return String(row?.entryId || row?.competitorId || row?.sk || "");
}

function canonicalEntryKey(row) {
  return String(row?.canonicalEntryId || row?.entryId || row?.sk || "").trim();
}

function entryDisplayLabel(row, entryById = new Map()) {
  const entry = entryById.get(canonicalEntryKey(row)) || entryById.get(String(row?.entryId || "").trim());
  const label = String(entry?.entryLabel || row?.entryLabel || "").trim();
  const attempt = Number(entry?.attemptIndex ?? row?.attemptIndex);
  if (label && label !== "Primary") {
    return label;
  }
  if (Number.isFinite(attempt) && attempt > 1) {
    return `Attempt ${attempt}`;
  }
  return "Primary";
}

function rowDisplayNameWithEntry(row, entryById = new Map()) {
  const name = String(row?.competitorName || row?.displayName || "Competitor").trim();
  const label = entryDisplayLabel(row, entryById);
  return label === "Primary" ? name : `${name} · ${label}`;
}

function scoreDetailPenaltyTotal(detail) {
  const score = detail?.scoreDetail || detail || {};
  // Use the scoring engine's uploaded total; target counts alone omit stage rules.
  const total = score.penaltyBreakdown?.totalPenaltySeconds;
  return total != null && Number.isFinite(Number(total)) ? Number(total) : null;
}

function buildStageDisplayRows(stageRows, stageItems = [], selectedStageId) {
  const stageNameById = new Map(
    stageItems.map((stage, index) => [
      stage.stageId,
      stageDisplayName(stage, index),
    ]),
  );

  return sortByTimeAsc(
    (Array.isArray(stageRows) ? stageRows : [])
      .filter((row) => (row.stageId || row.matchStageId) === selectedStageId && isRankEligibleRow(row))
      .map((row) => {
      const stageId = row?.stageId || row?.matchStageId || "";
      const stageName = stageNameById.get(stageId) || row?.stageName || "Stage score";
      const stageScore = Number(row?.stageScore ?? row?.totalScore ?? 0);
      return {
        ...row,
        totalScore: stageScore,
        scoreDisplay: row?.scoreDisplay || formatTime(stageScore),
        identityMetaItems: [
          stageName,
          ...(Array.isArray(row?.identityMetaItems) ? row.identityMetaItems : []),
        ].filter(Boolean),
      };
    }),
    "totalScore",
  );
}

function extractGroupValue(row, groupBy) {
  if (groupBy === "division") {
    return String(row?.divisionId || "").trim();
  }
  return "";
}

function buildGroupOptions(rows, groupBy) {
  if (groupBy === "all") {
    return [];
  }

  const values = new Set();
  rows.forEach((row) => {
    const value = extractGroupValue(row, groupBy);
    if (value) {
      values.add(value);
    }
  });

  return Array.from(values).sort((a, b) => a.localeCompare(b));
}

function applyGroupFilter(rows, groupBy, groupValue) {
  if (groupBy === "all") {
    return rows;
  }

  const target = String(groupValue || "").trim();
  if (!target) {
    return rows;
  }

  return rows.filter((row) => extractGroupValue(row, groupBy) === target);
}

function computeTimePerformancePercent(timeValue, leaderTime) {
  const time = Number(timeValue);
  const leader = Number(leaderTime);
  if (
    !Number.isFinite(time) ||
    time <= 0 ||
    !Number.isFinite(leader) ||
    leader <= 0
  ) {
    return null;
  }
  return (leader / time) * 100;
}

function CompetitorOverviewList({
  rows,
  scoreField,
  buildDetailPath,
  emptyText,
  rankMode = "ranked",
  stageProgressByKey = new Map(),
  requiredStageCount = 0,
  leaderScore = null,
  includePartial = false,
}) {
  const visibleRows = useMemo(
    () => includePartial ? rows : rows.filter(isLeaderboardRowComplete),
    [rows, includePartial],
  );

  if (!visibleRows.length) {
    return <div>{emptyText}</div>;
  }

  return (
    <div className="rank-list">
      {visibleRows.map((row, index) => {
        const competitor = row.competitorName || "Competitor";
        const division = String(row?.divisionId || "").trim();
        const teamName = String(row?.teamName || "").trim();
        const ambassadorLabel = row?.isNraAmbassador ? "NRA Ambassador" : "";
        const score = row?.scoreDisplay || formatTime(row?.[scoreField]);
        const performance = computeTimePerformancePercent(
          row?.[scoreField],
          leaderScore,
        );
        const rowKey = leaderboardRowKey(row);
        const stageCount = stageProgressByKey.get(rowKey) || 0;
        const progressLabel =
          requiredStageCount > 0
            ? `${stageCount}/${requiredStageCount} stages`
            : null;
        const rankLabel = rankMode === "ranked" ? `#${index + 1}` : "NR";
        const outcomeLabel = formatOutcomeStatus(row);

        return (
          <details key={`${row.sk}-${index}`} className="rank-item">
            <summary className="rank-summary">
              <span className="rank-chip">{rankLabel}</span>
              <span className="rank-main">
                <span className="rank-name">{competitor}</span>
                {(division || teamName || ambassadorLabel) && (
                  <span className="rank-meta-row">
                    {division && (
                      <span className="rank-division">{division}</span>
                    )}
                    {teamName && (
                      <span className="rank-meta-chip">{teamName}</span>
                    )}
                    {ambassadorLabel && (
                      <span className="rank-meta-chip rank-meta-chip-green">
                        {ambassadorLabel}
                      </span>
                    )}
                  </span>
                )}
              </span>
              <span className="rank-score">
                <span className="rank-time">{score}</span>
                {progressLabel && (
                  <span className="rank-progress">{progressLabel}</span>
                )}
                {outcomeLabel && (
                  <span className="rank-progress rank-progress-alert">{outcomeLabel}</span>
                )}
              </span>
            </summary>
            <div className="rank-detail-grid">
              <div className="rank-detail-cell">
                <div className="rank-detail-label">Time</div>
                <div className="rank-detail-value">{score}</div>
              </div>
              <div className="rank-detail-cell">
                <div className="rank-detail-label">A-Zone Hits</div>
                <div className="rank-detail-value">
                  {formatNumber(row.xCount)}
                </div>
              </div>
              <div className="rank-detail-cell">
                <div className="rank-detail-label">Tie Breaker</div>
                <div className="rank-detail-value">
                  {formatNumber(row.tieBreaker)}
                </div>
              </div>
              {rankMode === "ranked" && (
                <div className="rank-detail-cell">
                  <div className="rank-detail-label">Performance</div>
                  <div className="rank-detail-value">
                    {formatPercent(performance)}
                  </div>
                </div>
              )}
            </div>
            <div className="rank-actions">
              <Link to={buildDetailPath(row)} className="detail-link">
                Open Full Score Detail
              </Link>
            </div>
          </details>
        );
      })}
    </div>
  );
}

function BannerHotspots({ areas }) {
  const resolvedAreas = Array.isArray(areas) && areas.length > 0
    ? areas
    : [
        {
          to: "/RangePocket",
          ariaLabel: "Open Range Pocket",
          className: "booth-banner-hotspot--full",
        },
      ];

  return (
    <div className="booth-banner-hotspots">
      {resolvedAreas.map((area) => {
        const className = ["booth-banner-hotspot", area.className].filter(Boolean).join(" ");
        const label = area.ariaLabel || area.title || "Open banner link";
        if (area.href) {
          return (
            <a
              key={`${area.href}-${className}`}
              className={className}
              href={area.href}
              aria-label={label}
              title={area.title || label}
              target={area.target}
              rel={area.rel}
            />
          );
        }

        return (
          <Link
            key={`${area.to || "/RangePocket"}-${className}`}
            to={area.to || "/RangePocket"}
            className={className}
            aria-label={label}
            title={area.title || label}
          />
        );
      })}
    </div>
  );
}

export function BoothLeaderboardDisplay({
  match,
  bannerContent = null,
  bannerSrc,
  bannerAlt = "NRA Challenge Leaderboard powered by RangePocket",
  bannerLinkAreas = null,
  rows,
  topCount,
  onTopCountChange,
  onExit,
  leaderScore,
  rowClassName = "",
  controlLabel = "Rows",
  teamColumnLabel = "Team",
  scoreColumnLabel = "Time",
  performanceColumnLabel = "Performance",
  emptyMessage = "No completed scores yet",
  showControls = true,
  autoHideControls = false,
  showPerformanceColumn = true,
  displayClassName = "",
  filterControls = null,
  sponsorBanners = [],
  onReachedEnd,
  scrollResetKey = "",
  displaySectionLabel = "",
  lastUpdatedLabel = "",
  rowLimitPerGroup = false,
}) {
  const showAllRows = topCount === "all";
  const numericTopCount = Number.parseInt(String(topCount || ""), 10);
  const resolvedTopCount = Number.isFinite(numericTopCount) && numericTopCount > 0 ? numericTopCount : 10;
  const displayStressMode = isLocalDisplayStressMode();
  const displayMatch = useMemo(
    () => (displayStressMode ? buildDisplayStressMatch(match) : match),
    [displayStressMode, match],
  );
  const displayRows = useMemo(
    () => {
      const safeRows = Array.isArray(rows) ? rows : [];
      return displayStressMode ? buildDisplayStressRows(safeRows) : safeRows;
    },
    [displayStressMode, rows],
  );
  const groupRowCounts = new Map();
  const visibleRows = showAllRows ? displayRows : rowLimitPerGroup
    ? displayRows.filter(row => {
      const count = (groupRowCounts.get(row.participationGroup) || 0) + 1;
      groupRowCounts.set(row.participationGroup, count);
      return count <= resolvedTopCount;
    })
    : displayRows.slice(0, resolvedTopCount);
  const resolvedBannerSrc = bannerSrc || DEFAULT_LEADERBOARD_BANNER_SRC;
  const scrollBodyRef = useRef(null);

  useEffect(() => {
    const node = scrollBodyRef.current;
    if (!node) {
      return undefined;
    }

    node.scrollTop = 0;

    let animationFrame = 0;
    let lastTimestamp = 0;
    let phase = "pause-top";
    let phaseUntil = performance.now() + 5000;
    let reachedEnd = false;
    const topPauseMs = 5000;
    const bottomPauseMs = 1800;
    const pixelsPerSecond = 100;

    const notifyReachedEnd = () => {
      if (reachedEnd) {
        return;
      }
      reachedEnd = true;
      onReachedEnd?.();
    };

    const step = (now) => {
      const target = scrollBodyRef.current;
      if (!target) {
        return;
      }

      const maxScroll = Math.max(0, target.scrollHeight - target.clientHeight);
      if (maxScroll <= 4) {
        target.scrollTop = 0;
        notifyReachedEnd();
        phase = "pause-top";
        phaseUntil = now + topPauseMs;
        lastTimestamp = now;
        animationFrame = window.requestAnimationFrame(step);
        return;
      }

      const deltaMs = lastTimestamp > 0 ? now - lastTimestamp : 16;
      lastTimestamp = now;

      if (phase !== "scroll") {
        if (now >= phaseUntil) {
          if (phase === "pause-bottom") {
            notifyReachedEnd();
            target.scrollTop = 0;
            phase = "pause-top";
            phaseUntil = now + topPauseMs;
          } else {
            phase = "scroll";
          }
        }
        animationFrame = window.requestAnimationFrame(step);
        return;
      }

      const next = target.scrollTop + (pixelsPerSecond * deltaMs) / 1000;
      if (next >= maxScroll) {
        target.scrollTop = maxScroll;
        phase = "pause-bottom";
        phaseUntil = now + bottomPauseMs;
      } else {
        target.scrollTop = next;
      }

      animationFrame = window.requestAnimationFrame(step);
    };

    animationFrame = window.requestAnimationFrame(step);
    return () => window.cancelAnimationFrame(animationFrame);
  }, [onReachedEnd, topCount, visibleRows.length, scrollResetKey]);

  return (
    <div className={`booth-leaderboard${showPerformanceColumn ? "" : " booth-leaderboard-no-performance"}${displayClassName ? ` ${displayClassName}` : ""}`}>
      <div className="booth-leaderboard-banner-shell">
        {bannerContent || (
          <img
            className="booth-leaderboard-banner"
            src={resolvedBannerSrc}
            alt={bannerAlt}
            loading="eager"
            decoding="async"
          />
        )}
        <BannerHotspots areas={bannerLinkAreas} />
      </div>

      <div className="booth-leaderboard-header">
        <div className="booth-match-line">
          <span className="booth-match-name">
            {displayMatch?.matchName || "Match Leaderboard"}
          </span>
          <span className="booth-match-location">
            {matchLocationLabel(displayMatch)}
          </span>
        </div>
        <div className="booth-display-meta">
          {displaySectionLabel && <strong className="booth-display-section">{displaySectionLabel}</strong>}
          {lastUpdatedLabel && (
            <span className="booth-last-updated">{lastUpdatedLabel}</span>
          )}
        </div>
        {showControls && (
          <div className={`booth-controls${autoHideControls ? " booth-controls-auto-hide" : ""}`}>
            {filterControls}
            <label className="booth-top-control">
              <span>{controlLabel}</span>
              <select
                value={showAllRows ? "all" : resolvedTopCount}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  onTopCountChange(nextValue === "all" ? "all" : Number.parseInt(nextValue, 10));
                }}
              >
                {[5, 10, 15, 20, 25].map((count) => (
                  <option key={count} value={count}>
                    {count}
                  </option>
                ))}
                <option value="all">All</option>
              </select>
            </label>
            <button type="button" className="booth-exit-button" onClick={onExit}>
              Exit
            </button>
          </div>
        )}
      </div>

      <div className="booth-leaderboard-table">
        <div className="booth-leaderboard-row booth-leaderboard-head">
          <span>Rank</span>
          <span>Competitor</span>
          <span>{teamColumnLabel}</span>
          <span>{scoreColumnLabel}</span>
          {showPerformanceColumn && <span>{performanceColumnLabel}</span>}
        </div>

        <div className="booth-leaderboard-body" ref={scrollBodyRef}>
          {visibleRows.length === 0 ? (
            <div className="booth-empty">
              <div className="owner-registration-section-label">No rows</div>
              <strong>{emptyMessage}</strong>
            </div>
          ) : (
            visibleRows.map((row, index) => {
              const performance = computeTimePerformancePercent(
                row?.totalScore,
                leaderScore,
              );
              const teamName = String(row?.teamName || "").trim();
              const identityMetaItems = Array.isArray(row?.identityMetaItems)
                ? row.identityMetaItems.map((item) => String(item || "").trim()).filter(Boolean)
                : [];
              const podiumRank = row.podiumRank ?? index + 1;
              const podiumClass =
                podiumRank > 0 && podiumRank <= 3 && !row.isTied
                  ? ` booth-leaderboard-row-podium booth-leaderboard-row-top-${podiumRank}`
                  : "";
              const rankLabel = row.rankDisplay || (
                index === 0
                  ? "1st"
                  : index === 1
                    ? "2nd"
                    : index === 2
                      ? "3rd"
                      : `#${index + 1}`);
              const rowKey = `${row.sk || row.entryId || row.competitorId || row.competitorExternalId || "row"}-${index}`;
              const sponsorBanner = sponsorBanners.length > 0 && (index + 1) % 10 === 0
                ? sponsorBanners[(Math.floor((index + 1) / 10) - 1) % sponsorBanners.length]
                : null;

              return (
                <React.Fragment key={rowKey}>
                  {row.groupHeading && <div className="booth-leaderboard-group-heading" role="heading" aria-level="2">{row.groupHeading}</div>}
                  <div
                    className={`booth-leaderboard-row${rowClassName ? ` ${rowClassName}` : ""}${podiumClass}`}
                  >
                    <span className="booth-rank">
                      <span className="booth-rank-medal">{rankLabel}</span>
                    </span>
                    <span className="booth-name">
                      <span className="booth-name-text">
                        {row.competitorName || "Competitor"}
                      </span>
                      {(teamName || identityMetaItems.length > 0 || row?.isNraAmbassador) && (
                        <span className="booth-name-meta">
                          {teamName && (
                            <span className="booth-team-mobile">{teamName}</span>
                          )}
                          {identityMetaItems.length > 0 && (
                            <span className="booth-identity-meta">{identityMetaItems.join(" · ")}</span>
                          )}
                          {row?.isNraAmbassador && (
                            <span className="booth-badge">NRA Ambassador</span>
                          )}
                        </span>
                      )}
                    </span>
                    <span>{teamName || "-"}</span>
                    <span className="booth-time">
                      {row?.scoreDisplay || formatTime(row?.totalScore)}
                    </span>
                    {showPerformanceColumn && (
                      <span>{row?.performanceDisplay || formatPercent(performance)}</span>
                    )}
                  </div>
                  {sponsorBanner?.src && (
                    <div className="booth-leaderboard-sponsor-banner">
                      {sponsorBanner.href ? (
                        <a
                          href={sponsorBanner.href}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={sponsorBanner.alt || "Open sponsor link"}
                        >
                          <img
                            src={sponsorBanner.src}
                            alt={sponsorBanner.alt || ""}
                            loading="lazy"
                          />
                        </a>
                      ) : (
                        <img
                          src={sponsorBanner.src}
                          alt={sponsorBanner.alt || ""}
                          loading="lazy"
                        />
                      )}
                    </div>
                  )}
                </React.Fragment>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export function BoothDisplayStatus({
  eyebrow = "Live leaderboard",
  title,
  message,
  actions,
  bannerContent = null,
  bannerSrc,
  bannerAlt = "NRA Challenge Leaderboard powered by RangePocket",
  bannerLinkAreas = null,
}) {
  const resolvedBannerSrc = bannerSrc || DEFAULT_LEADERBOARD_BANNER_SRC;

  return (
    <div className="booth-leaderboard booth-leaderboard-status">
      <div className="booth-leaderboard-banner-shell">
        {bannerContent || (
          <img
            className="booth-leaderboard-banner"
            src={resolvedBannerSrc}
            alt={bannerAlt}
            loading="eager"
            decoding="async"
          />
        )}
        <BannerHotspots areas={bannerLinkAreas} />
      </div>
      <section className="booth-status-panel">
        <div className="owner-registration-section-label">{eyebrow}</div>
        <h1>{title}</h1>
        {message && <p>{message}</p>}
        {actions && <div className="booth-status-actions">{actions}</div>}
      </section>
    </div>
  );
}

function LeaderboardMatchDetail() {
  const { matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [matchRows, setMatchRows] = useState([]);
  const [stageRows, setStageRows] = useState([]);
  const [allAttemptMatchRows, setAllAttemptMatchRows] = useState([]);
  const [allAttemptStageRows, setAllAttemptStageRows] = useState([]);
  const [competitionEntries, setCompetitionEntries] = useState([]);
  const [analysisReadState, setAnalysisReadState] = useState({ loading: false, error: "", matchAvailable: false, stageAvailable: false });
  const [analysisRevision, setAnalysisRevision] = useState(0);
  const [matchSetup, setMatchSetup] = useState(null);
  const [registrationSummary, setRegistrationSummary] = useState(null);
  const [registrationSummaryError, setRegistrationSummaryError] = useState("");
  const [reloadRevision, setReloadRevision] = useState(0);
  const [stageSetupById, setStageSetupById] = useState({});
  const [loadingMatch, setLoadingMatch] = useState(true);
  const [loadingLeaderboards, setLoadingLeaderboards] = useState(true);
  const [matchError, setMatchError] = useState("");
  const [leaderboardError, setLeaderboardError] = useState("");

  const [scoreView, setScoreView] = useState("match");
  const [selectedScoreStageId, setSelectedScoreStageId] = useState("");
  const [selectedSetupStageId, setSelectedSetupStageId] = useState("");
  const [detailPanel, setDetailPanel] = useState(null);
  const [leaderboardSquadChoice, setLeaderboardSquadChoice] = useState("all");
  const [matchGroupBy, setMatchGroupBy] = useState("all");
  const [matchGroupValue, setMatchGroupValue] = useState("");
  const [stageGroupBy, setStageGroupBy] = useState("all");
  const [stageGroupValue, setStageGroupValue] = useState("");
  const [myRegistration, setMyRegistration] = useState(null);
  const [personalLoading, setPersonalLoading] = useState(true);
  const [personalError, setPersonalError] = useState("");
  const [signedIn, setSignedIn] = useState(false);
  const [lastScoresChecked, setLastScoresChecked] = useState(null);
  const [stageAnalysisExpanded, setStageAnalysisExpanded] = useState(false);
  const [selectedAnalysisEntryIds, setSelectedAnalysisEntryIds] = useState(null);
  const [analysisDetailByKey, setAnalysisDetailByKey] = useState({});
  const [loadingAnalysisDetails, setLoadingAnalysisDetails] = useState(false);
  const [analysisDetailError, setAnalysisDetailError] = useState("");

  const loading = loadingMatch || loadingLeaderboards;
  const error = matchError || leaderboardError;
  const resultsPhase = useMemo(() => isResultsPhaseStatus(match?.status), [match?.status]);
  const showLeaderboard = true;
  useMatchPageRefresh(match?.matchId === matchId ? match : null,
    () => setReloadRevision(value => value + 1), loadingMatch || loadingLeaderboards);

  useEffect(() => {
    setMatch(null); setMatchRows([]); setStageRows([]); setLastScoresChecked(null);
    setMyRegistration(null); setSignedIn(false); setPersonalError(""); setPersonalLoading(true);
  }, [matchId]);

  useEffect(() => {
    let active = true;

    async function load() {
      setLoadingMatch(true);
      setMatchError("");
      setRegistrationSummaryError("");

      try {
        const [matchResult, registrationResult] = await Promise.allSettled([
          getMatch(matchId),
          getMatchRegistrationSummary(matchId),
        ]);

        if (!active) {
          return;
        }

        if (matchResult.status === "rejected") throw matchResult.reason;
        const matchValue = matchResult.value;
        setMatch(matchValue);
        setRegistrationSummary(registrationResult.status === "fulfilled" ? registrationResult.value || null : null);
        if (registrationResult.status === "rejected") {
          setRegistrationSummaryError(registrationResult.reason?.message || "Registration counts could not load.");
        }

        if (matchValue?.matchSetupS3Key) {
          setMatchSetup({ loading: true, error: "", data: null });
          try {
            const setup = await getMatchSetupDetail(matchId);
            if (!active) {
              return;
            }
            setMatchSetup({ loading: false, error: "", data: setup });
          } catch (setupError) {
            if (!active) {
              return;
            }
            setMatchSetup({
              loading: false,
              error: setupError.message || "Failed to load match setup",
              data: null,
            });
          }
        } else {
          setMatchSetup({
            loading: false,
            error: "No match setup key on this record",
            data: null,
          });
        }
      } catch (err) {
        if (!active) {
          return;
        }

        if ([401, 403, 404].includes(err.status)) setMatch(null);
        setMatchError([403, 404].includes(err.status) ? "This match is unavailable. It may be private or no longer shared. Sign in with the owner account to manage a private match." : err.message || "Failed to load match");
      } finally {
        if (active) {
          setLoadingMatch(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [matchId, reloadRevision]);

  useEffect(() => {
    let active = true;

    async function loadMyRegistrationState() {
      try {
        const user = await getCurrentUser().catch(() => null);
        if (!active) return;
        setSignedIn(Boolean(user));
        if (!user) { setMyRegistration(null); setPersonalError(""); return; }
        const attrs = await fetchUserAttributes();
        const resolvedUserId = String(attrs?.sub || user?.userId || "").trim();
        if (!resolvedUserId) {
          if (active) {
            setMyRegistration(null);
          }
          return;
        }

        const registration = await getMyMatchRegistration(matchId, resolvedUserId);
        if (active) {
          setMyRegistration(registration || null);
          setPersonalError("");
        }
      } catch {
        if (active) {
          setPersonalError("Your registration could not load.");
        }
      } finally {
        if (active) setPersonalLoading(false);
      }
    }

    loadMyRegistrationState();
    return () => {
      active = false;
    };
  }, [matchId, reloadRevision]);

  const stageItems = useMemo(
    () => buildStageItems(match, stageRows),
    [match, stageRows],
  );
  const completedMatchRows = useMemo(
    () => matchRows.filter(isLeaderboardRowComplete),
    [matchRows],
  );
  const completedStageRows = useMemo(
    () => stageRows.filter(isLeaderboardRowComplete),
    [stageRows],
  );
  const availableSquads = useMemo(() => normalizeMatchSquads(match), [match]);
  const registrationFacts = useMemo(
    () => buildArcRegistrationFacts(registrationSummary, availableSquads),
    [registrationSummary, availableSquads],
  );
  const activeLeaderboardSquad = useMemo(
    () => findSquadByChoice(availableSquads, leaderboardSquadChoice),
    [availableSquads, leaderboardSquadChoice],
  );
  const activeLeaderboardSquadParams = useMemo(
    () => buildSquadPayload(activeLeaderboardSquad),
    [activeLeaderboardSquad],
  );

  useEffect(() => {
    if (leaderboardSquadChoice === "all") {
      return;
    }

    if (!findSquadByChoice(availableSquads, leaderboardSquadChoice)) {
      setLeaderboardSquadChoice("all");
    }
  }, [availableSquads, leaderboardSquadChoice]);

  useEffect(() => {
    let active = true;

    async function loadLeaderboards() {
      setLoadingLeaderboards(true);
      setLeaderboardError("");

      try {
        const [
          matchLeaderboardResult,
          stageLeaderboardResult,
          canonicalResult,
        ] = await Promise.allSettled([
          listMatchLeaderboards({
            matchId,
            squadId: activeLeaderboardSquadParams.squadId || null,
            squadNumber: activeLeaderboardSquadParams.squadNumber ?? null,
          }),
          listStageLeaderboards({
            matchId,
            squadId: activeLeaderboardSquadParams.squadId || null,
            squadNumber: activeLeaderboardSquadParams.squadNumber ?? null,
          }),
          listCompetitionResults(matchId),
        ]);

        if (!active) {
          return;
        }

        if (stageLeaderboardResult.status === "rejected") throw stageLeaderboardResult.reason;
        if (matchLeaderboardResult.status === "rejected" && canonicalResult.status === "rejected") {
          throw matchLeaderboardResult.reason;
        }

        const legacyMatchRows = matchLeaderboardResult.status === "fulfilled" && Array.isArray(matchLeaderboardResult.value)
          ? matchLeaderboardResult.value
          : [];
        const canonicalRows = canonicalResult.status === "fulfilled"
          ? normalizeCanonicalArcResultRows(canonicalResult.value, {
              squadId: activeLeaderboardSquadParams.squadId,
              squadNumber: activeLeaderboardSquadParams.squadNumber,
            })
          : [];
        const legacyStageRows = stageLeaderboardResult.status === "fulfilled" && Array.isArray(stageLeaderboardResult.value)
          ? stageLeaderboardResult.value
          : [];

        setMatchRows(legacyMatchRows.length > 0 ? sortByTimeAsc(legacyMatchRows, "totalScore") : canonicalRows);
        setStageRows(legacyStageRows);
        setLastScoresChecked(new Date().toISOString());
      } catch (err) {
        if (!active) {
          return;
        }

        setLeaderboardError(err.message || "Failed to load leaderboard data");
      } finally {
        if (active) {
          setLoadingLeaderboards(false);
        }
      }
    }

    loadLeaderboards();

    return () => {
      active = false;
    };
  }, [
    matchId,
    reloadRevision,
    activeLeaderboardSquadParams.squadId,
    activeLeaderboardSquadParams.squadNumber,
  ]);

  useEffect(() => {
    if (!stageAnalysisExpanded) return;
    let active = true;
    setAnalysisReadState({ loading: true, error: "", matchAvailable: false, stageAvailable: false });
    setAllAttemptMatchRows([]);
    setAllAttemptStageRows([]);
    setCompetitionEntries([]);
    setAnalysisDetailByKey({});
    setAnalysisDetailError("");

    async function loadAnalysis() {
      const params = {
        matchId, includeAllEntries: true,
        squadId: activeLeaderboardSquadParams.squadId || null,
        squadNumber: activeLeaderboardSquadParams.squadNumber ?? null,
      };
      const [canonicalResult, matchResult, stageResult, entriesResult] = await Promise.allSettled([
        listCompetitionResults(matchId, { includeAllEntries: true }),
        listMatchLeaderboards(params),
        listStageLeaderboards(params),
        listCompetitionEntries(matchId),
      ]);
      if (!active) return;
      const canonicalRows = canonicalResult.status === "fulfilled" ? normalizeCanonicalArcResultRows(canonicalResult.value, params) : [];
      const matchRows = matchResult.status === "fulfilled" && Array.isArray(matchResult.value) ? matchResult.value : [];
      const stageRows = stageResult.status === "fulfilled" && Array.isArray(stageResult.value) ? stageResult.value : [];
      const matchAvailable = matchResult.status === "fulfilled" || canonicalResult.status === "fulfilled";
      const stageAvailable = stageResult.status === "fulfilled";
      const warnings = [];
      if (!matchAvailable) warnings.push("Match attempt totals could not load.");
      if (!stageAvailable) warnings.push("Stage attempt history could not load.");
      // Entry labels require authentication; anonymous public scores remain usable without them.
      if (entriesResult.status === "rejected" && ![401, 403].includes(entriesResult.reason?.status)
          && entriesResult.reason?.message !== "Sign in required") warnings.push("Attempt names could not load.");
      setAllAttemptMatchRows(sortByTimeAsc(matchRows.length ? matchRows : canonicalRows, "totalScore"));
      setAllAttemptStageRows(stageRows);
      setCompetitionEntries(entriesResult.status === "fulfilled" && Array.isArray(entriesResult.value) ? entriesResult.value : []);
      setAnalysisReadState({ loading: false, error: warnings.join(" "), matchAvailable, stageAvailable });
    }
    loadAnalysis();
    return () => { active = false; };
  }, [matchId, reloadRevision, analysisRevision, stageAnalysisExpanded,
    activeLeaderboardSquadParams.squadId, activeLeaderboardSquadParams.squadNumber]);

  const stageRowsByStageId = useMemo(() => {
    const map = new Map();
    completedStageRows.forEach((row) => {
      const key = row.stageId || row.matchStageId || "unknown-stage";
      if (!map.has(key)) {
        map.set(key, []);
      }
      map.get(key).push(row);
    });
    return map;
  }, [completedStageRows]);

  const selectedScoreStageRows = useMemo(
    () =>
      sortByTimeAsc(
        stageRowsByStageId.get(selectedScoreStageId) || [],
        "stageScore",
      ),
    [stageRowsByStageId, selectedScoreStageId],
  );
  const selectedScoreStage = useMemo(
    () => {
      const summary = stageItems.find((stage) => stage.stageId === selectedScoreStageId);
      const setup = stageSetupById[selectedScoreStageId]?.data?.setupDetail;
      return summary ? { ...summary, ...(setup?.stage || (setup?.stageId ? setup : {})) } : null;
    },
    [stageItems, selectedScoreStageId, stageSetupById],
  );
  const entryById = useMemo(() => {
    const map = new Map();
    (Array.isArray(competitionEntries) ? competitionEntries : []).forEach((entry) => {
      const entryId = String(entry?.entryId || "").trim();
      if (entryId) {
        map.set(entryId, entry);
      }
    });
    return map;
  }, [competitionEntries]);
  const allAttemptStageRowsForSelectedStage = useMemo(
    () =>
      sortByTimeAsc(
        (Array.isArray(allAttemptStageRows) ? allAttemptStageRows : [])
          .filter((row) => String(row?.stageId || row?.matchStageId || "") === selectedScoreStageId),
        "stageScore",
      ),
    [allAttemptStageRows, selectedScoreStageId],
  );
  const defaultAnalysisEntryIds = useMemo(() => allAttemptStageRowsForSelectedStage
    .filter(isAnalysisBaselineEligible)
    .slice(0, 4).map(canonicalEntryKey), [allAttemptStageRowsForSelectedStage]);
  const effectiveAnalysisEntryIds = selectedAnalysisEntryIds ?? defaultAnalysisEntryIds;
  const selectedAnalysisRows = useMemo(() => {
    const selectedSet = new Set(effectiveAnalysisEntryIds);
    return allAttemptStageRowsForSelectedStage.filter((row) => selectedSet.has(canonicalEntryKey(row)));
  }, [allAttemptStageRowsForSelectedStage, effectiveAnalysisEntryIds]);
  const analysisBaselineRow = useMemo(
    () => selectedAnalysisRows.find(isAnalysisBaselineEligible) || null,
    [selectedAnalysisRows],
  );
  const allAttemptCount = useMemo(() => {
    const keys = new Set((Array.isArray(allAttemptMatchRows) ? allAttemptMatchRows : []).map(canonicalEntryKey).filter(Boolean));
    return keys.size;
  }, [allAttemptMatchRows]);

  useEffect(() => {
    if (loading) return;
    const firstStageId = stageItems[0]?.stageId || "";
    if (!stageItems.some(stage => stage.stageId === selectedScoreStageId)) {
      setSelectedScoreStageId(firstStageId);
    }
    if (!stageItems.some(stage => stage.stageId === selectedSetupStageId)) {
      setSelectedSetupStageId(firstStageId);
    }
  }, [loading, stageItems, selectedScoreStageId, selectedSetupStageId]);

  useEffect(() => {
    setSelectedAnalysisEntryIds(null);
    setAnalysisDetailByKey({});
    setAnalysisDetailError("");
  }, [selectedScoreStageId]);

  const loadSelectedAnalysisDetails = useCallback(async () => {
    const rows = selectedAnalysisRows;
    if (!rows.length) {
      return;
    }

    setLoadingAnalysisDetails(true);
    setAnalysisDetailError("");
    try {
      const detailPairs = await Promise.all(rows.map(async (row) => {
        const key = `${row.matchStageId || row.stageId}:${row.sk}`;
        const detail = await getStageScoreDetail(row.matchStageId || row.stageId, row.sk);
        return [key, detail];
      }));
      setAnalysisDetailByKey((prev) => ({
        ...prev,
        ...Object.fromEntries(detailPairs),
      }));
    } catch (err) {
      setAnalysisDetailError(err.message || "Failed to load selected score details.");
    } finally {
      setLoadingAnalysisDetails(false);
    }
  }, [selectedAnalysisRows]);

  const loadStageSetup = useCallback(
    async (stageId) => {
      if (!stageId) {
        return;
      }

      setStageSetupById((prev) => ({
        ...prev,
        [stageId]: {
          loading: true,
          error: "",
          data: prev[stageId]?.data || null,
        },
      }));

      try {
        const setup = await getStageSetupDetail(matchId, stageId);
        setStageSetupById((prev) => ({
          ...prev,
          [stageId]: { loading: false, error: "", data: setup },
        }));
      } catch (stageError) {
        setStageSetupById((prev) => ({
          ...prev,
          [stageId]: {
            loading: false,
            error: stageError.message || "Failed to load stage setup",
            data: null,
          },
        }));
      }
    },
    [matchId],
  );

  useEffect(() => {
    if (detailPanel !== "stage" || !selectedSetupStageId) {
      return;
    }

    const existing = stageSetupById[selectedSetupStageId];
    if (existing?.data || existing?.loading || existing?.error) {
      return;
    }

    loadStageSetup(selectedSetupStageId);
  }, [
    detailPanel,
    selectedSetupStageId,
    stageSetupById,
    loadStageSetup,
  ]);

  useEffect(() => {
    if (scoreView !== "stage" || !selectedScoreStageId) return;
    const existing = stageSetupById[selectedScoreStageId];
    if (existing?.data || existing?.loading || existing?.error) return;
    loadStageSetup(selectedScoreStageId);
  }, [scoreView, selectedScoreStageId, stageSetupById, loadStageSetup]);

  const requiredStageCount = useMemo(() => {
    const fromMatch = Array.isArray(match?.stages) ? match.stages.length : 0;
    if (fromMatch > 0) {
      return fromMatch;
    }
    return stageItems.length;
  }, [match, stageItems]);

  const stageProgressByKey = useMemo(() => {
    const map = new Map();
    for (const row of completedStageRows) {
      const key = leaderboardRowKey(row);
      const stageId = row?.stageId || row?.matchStageId;
      if (!key || !stageId) {
        continue;
      }
      if (!map.has(key)) {
        map.set(key, new Set());
      }
      map.get(key).add(stageId);
    }

    const countMap = new Map();
    for (const [key, set] of map.entries()) {
      countMap.set(key, set.size);
    }
    return countMap;
  }, [completedStageRows]);

  const rankedMatchRows = useMemo(() => {
    if (requiredStageCount <= 0) {
      return sortByTimeAsc(completedMatchRows.filter(isRankEligibleRow), "totalScore");
    }

    return sortByTimeAsc(
      completedMatchRows.filter(
          (row) =>
          isRankEligibleRow(row) &&
          (row?.canonicalAggregate === true ||
            (stageProgressByKey.get(leaderboardRowKey(row)) || 0) >=
            requiredStageCount),
      ),
      "totalScore",
    );
  }, [completedMatchRows, requiredStageCount, stageProgressByKey]);

  const unrankedMatchRows = useMemo(() => {
    if (requiredStageCount <= 0) {
      return [];
    }

    return sortByTimeAsc(
      matchRows.filter(
          (row) =>
          isRankEligibleRow(row) &&
          !rankedMatchRows.some(ranked => leaderboardRowKey(ranked) === leaderboardRowKey(row)) &&
          (stageProgressByKey.get(leaderboardRowKey(row)) || 0) > 0,
      ),
      "totalScore",
    );
  }, [matchRows, rankedMatchRows, requiredStageCount, stageProgressByKey]);

  const outcomeUnrankedMatchRows = useMemo(
    () => sortByTimeAsc(matchRows.filter((row) => !isRankEligibleRow(row)), "totalScore"),
    [matchRows],
  );

  const matchGroupOptions = useMemo(
    () =>
      buildGroupOptions(
        [...rankedMatchRows, ...unrankedMatchRows, ...outcomeUnrankedMatchRows],
        matchGroupBy,
      ),
    [rankedMatchRows, unrankedMatchRows, outcomeUnrankedMatchRows, matchGroupBy],
  );

  useEffect(() => {
    if (matchGroupBy === "all") {
      setMatchGroupValue("");
      return;
    }
    if (!matchGroupOptions.includes(matchGroupValue)) {
      setMatchGroupValue(matchGroupOptions[0] || "");
    }
  }, [matchGroupBy, matchGroupOptions, matchGroupValue]);

  const filteredRankedMatchRows = useMemo(
    () => applyGroupFilter(rankedMatchRows, matchGroupBy, matchGroupValue),
    [rankedMatchRows, matchGroupBy, matchGroupValue],
  );

  const filteredUnrankedMatchRows = useMemo(
    () => applyGroupFilter(unrankedMatchRows, matchGroupBy, matchGroupValue),
    [unrankedMatchRows, matchGroupBy, matchGroupValue],
  );

  const filteredOutcomeUnrankedMatchRows = useMemo(
    () => applyGroupFilter(outcomeUnrankedMatchRows, matchGroupBy, matchGroupValue),
    [outcomeUnrankedMatchRows, matchGroupBy, matchGroupValue],
  );

  const stageGroupOptions = useMemo(
    () => buildGroupOptions(selectedScoreStageRows, stageGroupBy),
    [selectedScoreStageRows, stageGroupBy],
  );

  useEffect(() => {
    if (stageGroupBy === "all") {
      setStageGroupValue("");
      return;
    }
    if (!stageGroupOptions.includes(stageGroupValue)) {
      setStageGroupValue(stageGroupOptions[0] || "");
    }
  }, [stageGroupBy, stageGroupOptions, stageGroupValue]);

  const filteredSelectedScoreStageRows = useMemo(
    () =>
      applyGroupFilter(selectedScoreStageRows, stageGroupBy, stageGroupValue),
    [selectedScoreStageRows, stageGroupBy, stageGroupValue],
  );

  const rankedSelectedScoreStageRows = useMemo(
    () => filteredSelectedScoreStageRows.filter(isRankEligibleRow),
    [filteredSelectedScoreStageRows],
  );
  const outcomeSelectedScoreStageRows = useMemo(
    () => filteredSelectedScoreStageRows.filter((row) => !isRankEligibleRow(row)),
    [filteredSelectedScoreStageRows],
  );

  const topTotal = filteredRankedMatchRows.length;
  const bestTime =
    topTotal > 0 ? formatNumber(filteredRankedMatchRows[0]?.totalScore) : null;
  const matchLeaderTime =
    topTotal > 0 ? formatNumber(filteredRankedMatchRows[0]?.totalScore) : null;
  const stageLeaderTime =
    rankedSelectedScoreStageRows.length > 0
      ? formatNumber(rankedSelectedScoreStageRows[0]?.stageScore)
      : null;

  const analysisPanel = (<section className="leaderboard-card arc-stage-analysis-panel">
                <div className="leaderboard-card-row leaderboard-card-row--wrap">
                  <div>
                    <div className="owner-registration-section-label">Score comparison</div>
                    <h2>Stage Analysis</h2>
                    <div className="leaderboard-meta">
                      Compare selected competitors and repeated attempts for the current stage.
                    </div>
                  </div>
                  <button
                    type="button"
                    className="registration-auth-secondary leaderboard-inline-link detail-link-button"
                    onClick={() => setStageAnalysisExpanded((value) => !value)}
                  >
                    {stageAnalysisExpanded ? "Hide analysis" : "Compare attempts"}
                  </button>
                </div>
                {stageAnalysisExpanded && (
                  <div className="arc-stage-analysis-body">
                    {analysisReadState.loading && <div role="status" className="leaderboard-meta">Loading attempt history...</div>}
                    {analysisReadState.error && <div role="alert" className="leaderboard-error-inline">
                      <strong>Analysis is incomplete</strong>
                      <p>{analysisReadState.error}</p>
                      <button type="button" className="registration-auth-secondary"
                        onClick={() => setAnalysisRevision((value) => value + 1)}>Retry analysis</button>
                    </div>}
                    <div className="registration-readiness-strip public-match-readiness-strip">
                      <div className="registration-readiness-item is-ready">
                        <span>Ranked in match</span>
                        <strong>{completedMatchRows.length}</strong>
                      </div>
                      <div className="registration-readiness-item is-neutral">
                        <span>Synced attempts</span>
                        <strong>{analysisReadState.matchAvailable ? allAttemptCount : "-"}</strong>
                      </div>
                      <div className="registration-readiness-item is-neutral">
                        <span>Stage attempts</span>
                        <strong>{analysisReadState.stageAvailable ? allAttemptStageRowsForSelectedStage.length : "-"}</strong>
                      </div>
                    </div>

                    <div className="leaderboard-filter-row arc-analysis-stage-selector">
                      <label className="match-filter-field leaderboard-filter-field">
                        <span>Stage</span>
                        <select
                          className="match-sort"
                          value={selectedScoreStageId}
                          onChange={(event) => setSelectedScoreStageId(event.target.value)}
                        >
                          {stageItems.map((stage, index) => (
                            <option key={stage.stageId} value={stage.stageId}>
                              {stageDisplayName(stage, index)}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    {selectedScoreStage && (
                      <h3 className="arc-analysis-selected-stage">
                        {stageDisplayName(selectedScoreStage, stageItems.findIndex((stage) => stage.stageId === selectedScoreStageId))}
                      </h3>
                    )}
                    {allAttemptStageRowsForSelectedStage.length > 0 ? (
                      <>
                        <div className="arc-analysis-entry-picker">
                          {allAttemptStageRowsForSelectedStage.map((row) => {
                            const key = canonicalEntryKey(row);
                            const selected = effectiveAnalysisEntryIds.includes(key);
                            return (
                              <button
                                key={`${key}-${row.sk}`}
                                type="button"
                                aria-pressed={selected}
                                className={`arc-analysis-entry-chip ${selected ? "active" : ""}`}
                                onClick={() => {
                                  setSelectedAnalysisEntryIds((prev) => {
                                    const current = prev ?? defaultAnalysisEntryIds;
                                    return current.includes(key)
                                      ? current.filter((item) => item !== key)
                                      : [...current, key];
                                  });
                                }}
                              >
                                <strong>{rowDisplayNameWithEntry(row, entryById)}</strong>
                                <span>{formatTime(row.stageScore)}s · {formatAnalysisScoreStatus(row)}</span>
                              </button>
                            );
                          })}
                        </div>
                        <div className="leaderboard-card-row leaderboard-card-row--wrap">
                          <div className="leaderboard-meta">
                            {selectedAnalysisRows.length > 0
                              ? `${selectedAnalysisRows.length} selected${selectedAnalysisEntryIds === null ? " · confirmed, rank-eligible defaults" : ""}.`
                              : "Select attempts to compare."}
                          </div>
                          <button
                            type="button"
                            className="registration-auth-secondary"
                            onClick={() => setSelectedAnalysisEntryIds(selectedAnalysisRows.length ? [] : null)}
                            disabled={!selectedAnalysisRows.length && !defaultAnalysisEntryIds.length}
                          >
                            {selectedAnalysisRows.length ? "Clear selection" : "Select defaults"}
                          </button>
                          <button
                            type="button"
                            className="match-search-button"
                            onClick={loadSelectedAnalysisDetails}
                            disabled={loadingAnalysisDetails || selectedAnalysisRows.length === 0}
                          >
                            {loadingAnalysisDetails ? "Loading details..." : "Load target breakdown"}
                          </button>
                        </div>
                        {analysisDetailError && (
                          <div className="leaderboard-error-inline">{analysisDetailError}</div>
                        )}
                        <div className="arc-stage-analysis-grid">
                          {selectedAnalysisRows.map((row, index) => {
                            const score = Number(row?.stageScore || 0);
                            const delta = analysisBaselineRow && isAnalysisBaselineEligible(row)
                              ? score - Number(analysisBaselineRow.stageScore) : null;
                            const detailKey = `${row.matchStageId || row.stageId}:${row.sk}`;
                            const detail = analysisDetailByKey[detailKey]?.scoreDetail || analysisDetailByKey[detailKey] || null;
                            const targetEntries = Array.isArray(detail?.targetEntries) ? detail.targetEntries : [];
                            const penaltyTotal = scoreDetailPenaltyTotal(detail);
                            return (
                              <article key={`${row.sk}-${index}`} className="arc-stage-analysis-card">
                                <div className="arc-analysis-card-head">
                                  <strong>{rowDisplayNameWithEntry(row, entryById)}</strong>
                                  <span>{formatTime(score)}s</span>
                                </div>
                                <div className="arc-analysis-metrics">
                                  <span>{formatAnalysisScoreStatus(row)}</span>
                                  <span>Stage time {detail?.penaltyBreakdown?.baseTimeSeconds != null ? `${formatTime(detail.penaltyBreakdown.baseTimeSeconds)}s` : "-"}</span>
                                  {detail?.recordedStageTimeSeconds != null && <span>Recorded {formatTime(detail.recordedStageTimeSeconds)}s</span>}
                                  <span>Penalty {penaltyTotal != null ? `${formatTime(penaltyTotal)}s` : "Not supplied"}</span>
                                  <span>{row === analysisBaselineRow ? "Baseline"
                                    : delta != null ? `${delta < 0 ? "-" : "+"}${formatTime(Math.abs(delta))}s`
                                      : analysisBaselineRow ? "Not compared" : "No confirmed baseline"}</span>
                                </div>
                                {targetEntries.length > 0 ? (
                                  <div className="arc-analysis-targets">
                                    {targetEntries.map((target) => (
                                      <span key={target.targetSlotKey}>
                                        <strong>{target.targetSlotKey}</strong>
                                        {String(target.inputKind || "").toLowerCase().includes("paper")
                                          ? ` A${target.zoneA || 0}${String(target.inputKind || "").includes("A/B/C/D") || Number(target.zoneB) > 0 ? ` B${target.zoneB || 0}` : ""} C${target.zoneC || 0} D${target.zoneD || 0} M${target.manualMisses || 0}`
                                          : ` H${target.steelHits || 0} M${target.manualMisses || 0}`}
                                        {Number(target.failureToEngageCount) > 0 && ` FTE ${target.failureToEngageCount}`}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <div className="leaderboard-meta">Load target breakdown to inspect target-level scoring.</div>
                                )}
                              </article>
                            );
                          })}
                        </div>
                      </>
                    ) : analysisReadState.stageAvailable && !analysisReadState.loading ? (
                      <div className="leaderboard-meta">
                        {allAttemptCount > 0
                          ? "Match-level attempts are available, but this stage does not have synced stage score cards to compare yet."
                          : "No attempts are available for stage comparison yet."}
                      </div>
                    ) : null}
                  </div>
                )}
              </section>);

  if (match?.matchId === matchId && match.lifecycle?.cancelledAt) return <CancelledMatchDetail match={match} family="arc" registration={myRegistration} />;

  if (match?.matchId === matchId && isCompletedDetail(match)) {
    return <CompletedMatchDetail key={matchId} match={match} family="arc"
      sections={completedArcSections(match, matchRows, stageRows, stageItems, rankedMatchRows)}
      summary={registrationSummary} summaryError={registrationSummaryError} squads={availableSquads}
      registration={myRegistration} signedIn={signedIn} personalLoading={personalLoading} personalError={personalError}
      setup={matchSetup?.data?.setupDetail} setupLoading={matchSetup?.loading} setupError={match?.matchSetupS3Key ? matchSetup?.error : ""}
      scoresLoading={loadingLeaderboards} scoresError={leaderboardError} onRefresh={() => setReloadRevision(value => value + 1)}
      analysis={analysisPanel} />;
  }

  if (match?.matchId === matchId && isInProgressDetail(match)) {
    return <InProgressMatchDetail key={matchId} match={match} family="arc"
      sections={arcLiveSections(match, matchRows, stageRows, stageItems)} summary={registrationSummary} registration={myRegistration}
      signedIn={signedIn} personalLoading={personalLoading} personalError={personalError}
      setup={matchSetup?.data?.setupDetail} setupLoading={matchSetup?.loading} setupError={match?.matchSetupS3Key ? matchSetup?.error : ""}
      scoresLoading={loadingLeaderboards} scoresError={leaderboardError} lastChecked={lastScoresChecked}
      detailsLoading={loadingMatch} detailsError={matchError || registrationSummaryError}
      onRefresh={() => setReloadRevision(value => value + 1)} />;
  }

  if (match?.matchId === matchId && isUpcomingDetail(match, matchRows.length > 0 || stageRows.length > 0)) {
    return <UpcomingMatchDetail key={matchId} match={match} family="arc" summary={registrationSummary} registration={myRegistration}
      registrationError={registrationSummaryError} setup={matchSetup?.data?.setupDetail} setupLoading={matchSetup?.loading}
      setupError={match?.matchSetupS3Key ? matchSetup?.error : ""} refreshError={matchError} onRetry={() => setReloadRevision((value) => value + 1)} />;
  }

  return (
    <section className="leaderboard-page arc-results-page">
      <Helmet>
        <title>{match?.matchName ? `${match.matchName} | Range Pocket Competition` : "Match Detail | Range Pocket Competition"}</title>
      </Helmet>

      {!loading && !error && (
      <><RelatedMatchSelector match={match} family="arc" />
      <div className="arc-print-results-header" aria-label="Printed result scope">
        <strong>{match?.matchName || "Match Leaderboard"}</strong>
        <span>{[match?.level, formatDate(match?.startDate), matchAddressLabel(match)].filter(Boolean).join(" · ")}</span>
        <span>{scoreView === "stage" ? selectedScoreStage ? stageDisplayName(selectedScoreStage, stageItems.indexOf(selectedScoreStage)) : "Stage Ranking" : "Match Ranking"}</span>
        <span>{[
          activeLeaderboardSquad ? formatSquadLabel(activeLeaderboardSquad) : "All squads",
          scoreView === "stage" ? stageGroupBy === "division" ? stageGroupValue : "All divisions" : matchGroupBy === "division" ? matchGroupValue : "All divisions",
        ].join(" · ")}</span>
      </div>
      <div className="leaderboard-detail-hero">
        <div className="leaderboard-detail-copy">
          <Link to="/matches" className="leaderboard-detail-back-link">
            <span aria-hidden="true">←</span>
            <span>Matches</span>
          </Link>
          <h1>{match?.matchName || "Match Leaderboard"}</h1>
          <div className="leaderboard-detail-chip-row">
            <span
              className={`status-pill status-${String(match?.status || "unknown").replace(/\s+/g, "_")}`}
            >
              {formatStatus(match?.status)}
            </span>
            {match?.level && (
              <span className="open-match-chip">{String(match.level).trim()}</span>
            )}
            {match?.sanctionedType && (
              <span className="open-match-chip">{String(match.sanctionedType).trim()}</span>
            )}
            {match?.tournamentId && (
              <Link
                to={`/tournaments/${encodeURIComponent(match.tournamentId)}`}
                className="open-match-chip leaderboard-inline-link"
              >
                {match.tournamentName || "Tournament"}
              </Link>
            )}
            {availableSquads.length > 0 && (
              <span className="open-match-chip">
                {availableSquads.length} squad
                {availableSquads.length === 1 ? "" : "s"}
              </span>
            )}
          </div>
          <div className="leaderboard-detail-facts leaderboard-detail-facts-stacked">
            <div>
              <span>Start</span>
              <strong>{formatDate(match?.startDate)}</strong>
            </div>
            <div>
              <span>End</span>
              <strong>{formatDate(match?.endDate)}</strong>
            </div>
          </div>
          <div className="leaderboard-detail-actions">
            {!resultsPhase && (
              <Link
                to={`/matches/arc/${encodeURIComponent(matchId)}/register`}
                className="match-search-button leaderboard-inline-link"
              >
                {myRegistration ? "Manage registration" : registrationFacts.registrationOpen ? "Register" : "Registration details"}
              </Link>
            )}
            <button
              type="button"
              className="match-search-button"
              onClick={() => setDetailPanel("match")}
            >
              Match Info
            </button>
            <button
              type="button"
              className="match-search-button"
              onClick={() => setDetailPanel("stage")}
            >
              Stage Info
            </button>
          </div>
        </div>
        <div className="leaderboard-detail-map-card">
          <MatchLocationMap
            matches={match ? [match] : []}
            selectedMatchId={match?.matchId || ""}
            className="leaderboard-detail-map"
            emptyMessage="This match does not have latitude and longitude yet."
          />
          <div className="leaderboard-detail-map-meta">
            <span>Address</span>
            <strong>{matchAddressLabel(match)}</strong>
          </div>
        </div>
      </div></>
      )}

      {loading && (
        <div className="leaderboard-card list-empty-panel status-loading-panel status-loading-panel--detail">
          <div className="owner-registration-section-label">Match detail</div>
          <strong>Loading match details and scores</strong>
          <span>Checking registration, venue, stages, and standings</span>
          <div className="status-loading-skeleton" aria-hidden="true">
            <i />
            <i />
            <i />
          </div>
        </div>
      )}
      {!loading && error && (
        <CompetitionErrorState
          eyebrow="Match detail"
          title={matchError ? "Match could not load" : "Scores could not load"}
          message={error}
          onRetry={() => setReloadRevision(value => value + 1)}
          links={[
            { to: `/auth/sign-in?returnTo=${encodeURIComponent(`/matches/arc/${matchId}`)}`, label: "Sign in" },
            { to: "/matches", label: "Matches" },
            { to: "/tournaments", label: "Tournaments" },
          ]}
          className="match-detail-error-panel"
        />
      )}

      {!loading && !error && (
        <>
          {!resultsPhase && (
            <>
              <div className="leaderboard-card leaderboard-inline-action-panel public-match-registration-panel">
                <div className="leaderboard-card-row leaderboard-card-row--wrap">
                  <h2>Registration</h2>
                  <Link
                    to={`/matches/arc/${encodeURIComponent(matchId)}/register`}
                    className="match-search-button leaderboard-inline-link"
                  >
                    {myRegistration ? "Manage" : "Register"}
                  </Link>
                </div>
                <div className="registration-readiness-strip public-match-readiness-strip">
                  <div className={`registration-readiness-item ${registrationFacts.registered > 0 ? "is-ready" : "is-neutral"}`}>
                    <span>Registered</span>
                    <strong>{registrationSummary ? registrationFacts.registered : "-"}</strong>
                  </div>
                  <div className={`registration-readiness-item ${registrationFacts.squadCount > 0 ? "is-ready" : "is-neutral"}`}>
                    <span>Squads</span>
                    <strong>{registrationFacts.squadCount > 0 ? registrationFacts.squadCount : "Not required"}</strong>
                  </div>
                  <div className={`registration-readiness-item ${Number.isFinite(registrationFacts.remaining) && registrationFacts.remaining > 0 ? "is-ready" : "is-neutral"}`}>
                    <span>Open Spots</span>
                    <strong>{registrationSummaryError ? "-" : Number.isFinite(registrationFacts.remaining) ? registrationFacts.remaining : "Capacity not listed"}</strong>
                  </div>
                  <div className={`registration-readiness-item ${registrationFacts.registrationOpen ? "is-ready" : "is-missing"}`}>
                    <span>Status</span>
                    <strong>{registrationSummaryError ? "Unavailable" : registrationFacts.registrationOpen ? "Open" : "Closed"}</strong>
                  </div>
                </div>
                {registrationSummaryError && (
                  <div role="alert" className="leaderboard-error-inline">
                    <p>Registration counts unavailable. {registrationSummaryError}</p>
                    <button type="button" className="registration-auth-secondary" onClick={() => setReloadRevision(value => value + 1)}>
                      Retry registration counts
                    </button>
                  </div>
                )}
              </div>
            </>
          )}

          {showLeaderboard && (
            <div className="match-stat-grid">
              <div className="match-stat-card">
                <div className="match-stat-label">Ranked</div>
                <div className="match-stat-value">{topTotal}</div>
              </div>
              <div className="match-stat-card">
                <div className="match-stat-label">Stages</div>
                <div className="match-stat-value">{stageItems.length}</div>
              </div>
              <div className="match-stat-card">
                <div className="match-stat-label">Incomplete</div>
                <div className="match-stat-value">{unrankedMatchRows.length}</div>
              </div>
              <div className="match-stat-card">
                <div className="match-stat-label">Best Time</div>
                <div className="match-stat-value">{bestTime == null ? "-" : formatTime(bestTime)}</div>
              </div>
            </div>
          )}

          {showLeaderboard && (
            <div className="leaderboard-stack">
              <section className="leaderboard-card leaderboard-results-action-bar">
                <div className="leaderboard-card-row leaderboard-card-row--wrap">
                  <h2>Results</h2>
                  <div className="leaderboard-display-action-group">
                    <Link
                      to={matchDisplayPath(matchId)}
                      className="match-search-button booth-launch-button"
                    >
                      Open standings
                    </Link>
                    <Link
                      to={matchDisplayPath(matchId, "all")}
                      className="registration-auth-secondary leaderboard-inline-link"
                    >
                      All scores
                    </Link>
                  </div>
                </div>
              </section>
              {analysisPanel}
              <section className="leaderboard-card arc-results-view-controls">
                <div
                  className="mode-switch"
                  role="tablist"
                  aria-label="Score view"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={scoreView === "match"}
                    className={`mode-switch-btn ${scoreView === "match" ? "active" : ""}`}
                    onClick={() => setScoreView("match")}
                  >
                    Match Ranking
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={scoreView === "stage"}
                    className={`mode-switch-btn ${scoreView === "stage" ? "active" : ""}`}
                    onClick={() => setScoreView("stage")}
                  >
                    Stage Ranking
                  </button>
                </div>
                {availableSquads.length > 0 && (
                  <div className="leaderboard-filter-row">
                    <label className="match-filter-field leaderboard-filter-field">
                      <span>Squad</span>
                      <select
                        className="match-sort"
                        value={leaderboardSquadChoice}
                        onChange={(event) =>
                          setLeaderboardSquadChoice(event.target.value)
                        }
                      >
                        <option value="all">All squads</option>
                        {availableSquads.map((squad) => (
                          <option
                            key={squadChoiceValue(squad)}
                            value={squadChoiceValue(squad)}
                          >
                            {formatSquadLabel(squad)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <div className="leaderboard-meta leaderboard-filter-note">
                      {activeLeaderboardSquad
                        ? `Showing scores for ${formatSquadLabel(activeLeaderboardSquad)}.`
                        : "Showing scores for all squads."}
                    </div>
                  </div>
                )}
              </section>

              {scoreView === "match" && (
                <section className="leaderboard-card">
                  <h2>Match Ranking (Complete Scores)</h2>
                  <div className="leaderboard-meta">
                    Competitors are ranked only after scoring all required
                    stages.
                  </div>
                  <div className="leaderboard-filter-row">
                    <label className="match-filter-field leaderboard-filter-field">
                      <span>Group</span>
                      <select
                        className="match-sort"
                        value={matchGroupBy}
                        onChange={(event) => setMatchGroupBy(event.target.value)}
                      >
                        <option value="all">All groups</option>
                        <option value="division">Division</option>
                      </select>
                    </label>
                    {matchGroupBy !== "all" && (
                      <label className="match-filter-field leaderboard-filter-field">
                        <span>Division</span>
                        <select
                          className="match-sort"
                          value={matchGroupValue}
                          onChange={(event) =>
                            setMatchGroupValue(event.target.value)
                          }
                        >
                          {matchGroupOptions.map((group) => (
                            <option key={group} value={group}>
                              {group}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                  <CompetitorOverviewList
                    rows={filteredRankedMatchRows}
                    scoreField="totalScore"
                    emptyText="No fully completed competitors yet."
                    rankMode="ranked"
                    stageProgressByKey={stageProgressByKey}
                    requiredStageCount={requiredStageCount}
                    leaderScore={matchLeaderTime}
                    buildDetailPath={(row) =>
                      `/matches/results/match/${encodeURIComponent(matchId)}/${encodeURIComponent(row.sk)}`
                    }
                  />

                  {filteredUnrankedMatchRows.length > 0 && (
                    <>
                      <h2>Partial Results (Unranked)</h2>
                      <CompetitorOverviewList
                        rows={filteredUnrankedMatchRows}
                        includePartial
                        scoreField="totalScore"
                        emptyText=""
                        rankMode="unranked"
                        stageProgressByKey={stageProgressByKey}
                        requiredStageCount={requiredStageCount}
                        leaderScore={matchLeaderTime}
                        buildDetailPath={(row) =>
                          `/matches/results/match/${encodeURIComponent(matchId)}/${encodeURIComponent(row.sk)}`
                        }
                      />
                    </>
                  )}

                  {filteredOutcomeUnrankedMatchRows.length > 0 && (
                    <>
                      <h2>Not Ranked</h2>
                      <div className="leaderboard-meta">
                        DQ, DNF, and withdrawn competitors are preserved for match records but excluded from standings.
                      </div>
                      <CompetitorOverviewList
                        rows={filteredOutcomeUnrankedMatchRows}
                        includePartial
                        scoreField="totalScore"
                        emptyText=""
                        rankMode="unranked"
                        stageProgressByKey={stageProgressByKey}
                        requiredStageCount={requiredStageCount}
                        buildDetailPath={(row) =>
                          `/matches/results/match/${encodeURIComponent(matchId)}/${encodeURIComponent(row.sk)}`
                        }
                      />
                    </>
                  )}
                </section>
              )}

              {scoreView === "stage" && (
                <section className="leaderboard-card">
                  <div className="leaderboard-card-row">
                    <h2>Stage Ranking</h2>
                    <label className="match-filter-field leaderboard-filter-field leaderboard-filter-field--inline">
                      <span>Stage</span>
                      <select
                        className="match-sort"
                        value={selectedScoreStageId}
                        onChange={(event) =>
                          setSelectedScoreStageId(event.target.value)
                        }
                      >
                        {stageItems.map((stage, index) => (
                          <option key={stage.stageId} value={stage.stageId}>
                            {stageDisplayName(stage, index)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>

                  <div className="leaderboard-meta">
                    {selectedScoreStage
                      ? `Timing: ${formatStageTimingMode(selectedScoreStage)}`
                      : "Timing: -"}
                  </div>
                  <div className="leaderboard-filter-row">
                    <label className="match-filter-field leaderboard-filter-field">
                      <span>Group</span>
                      <select
                        className="match-sort"
                        value={stageGroupBy}
                        onChange={(event) => setStageGroupBy(event.target.value)}
                      >
                        <option value="all">All groups</option>
                        <option value="division">Division</option>
                      </select>
                    </label>
                    {stageGroupBy !== "all" && (
                      <label className="match-filter-field leaderboard-filter-field">
                        <span>Division</span>
                        <select
                          className="match-sort"
                          value={stageGroupValue}
                          onChange={(event) =>
                            setStageGroupValue(event.target.value)
                          }
                        >
                          {stageGroupOptions.map((group) => (
                            <option key={group} value={group}>
                              {group}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}
                  </div>
                  <CompetitorOverviewList
                    rows={rankedSelectedScoreStageRows}
                    scoreField="stageScore"
                    emptyText="No ranked stage scores yet"
                    leaderScore={stageLeaderTime}
                    buildDetailPath={(row) =>
                      `/matches/results/stage/${encodeURIComponent(row.matchStageId)}/${encodeURIComponent(row.sk)}`
                    }
                  />
                  {outcomeSelectedScoreStageRows.length > 0 && (
                    <>
                      <h2>Not Ranked</h2>
                      <CompetitorOverviewList
                        rows={outcomeSelectedScoreStageRows}
                        scoreField="stageScore"
                        emptyText=""
                        rankMode="unranked"
                        buildDetailPath={(row) =>
                          `/matches/results/stage/${encodeURIComponent(row.matchStageId)}/${encodeURIComponent(row.sk)}`
                        }
                      />
                    </>
                  )}
                </section>
              )}
            </div>
          )}

        </>
      )}

      {detailPanel === "match" && (
        <DetailOverlay title="Match Info" onClose={() => setDetailPanel(null)}>
          {matchSetup?.loading && (
            <div className="status-loading-panel">
              <div className="owner-registration-section-label">Loading</div>
              <strong>Match Setup</strong>
            </div>
          )}
          {!matchSetup?.loading && matchSetup?.error && (
            <div className="leaderboard-error-inline">{matchSetup.error}</div>
          )}
          {!matchSetup?.loading && matchSetup?.data && (
            <MatchSetupRenderer detail={matchSetup.data} hideTitle />
          )}
        </DetailOverlay>
      )}

      {detailPanel === "stage" && (
        <DetailOverlay title="Stage Info" onClose={() => setDetailPanel(null)}>
          <div className="detail-overlay-controls">
            <select
              className="match-sort"
              value={selectedSetupStageId}
              onChange={(event) => setSelectedSetupStageId(event.target.value)}
            >
              {stageItems.map((stage, index) => (
                <option key={stage.stageId} value={stage.stageId}>
                  {stageDisplayName(stage, index)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="match-search-button"
              disabled={!selectedSetupStageId || stageSetupById[selectedSetupStageId]?.loading}
              onClick={() => loadStageSetup(selectedSetupStageId)}
            >
              {stageSetupById[selectedSetupStageId]?.loading ? "Loading..." : "Refresh"}
            </button>
          </div>
          {stageItems.length === 0 && (
            <div className="match-detail-empty-panel list-empty-panel">
              <div className="owner-registration-section-label">Stage Info</div>
              <strong>No stage metadata found.</strong>
            </div>
          )}
          {selectedSetupStageId && stageSetupById[selectedSetupStageId]?.error && (
            <div className="leaderboard-error-inline">
              {stageSetupById[selectedSetupStageId].error}
            </div>
          )}
          {selectedSetupStageId && stageSetupById[selectedSetupStageId]?.data && (
            <StageSetupRenderer
              detail={stageSetupById[selectedSetupStageId].data}
              hideTitle
            />
          )}
        </DetailOverlay>
      )}
    </section>
  );
}

function LeaderboardMatchDisplay() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [match, setMatch] = useState(null);
  const [matchRows, setMatchRows] = useState([]);
  const [stageRows, setStageRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [topCount, setTopCount] = useState(() => normalizeDisplayRowCount(getDisplayRowsParam(searchParams), 20));
  const [readyBannerSrc, setReadyBannerSrc] = useState(DEFAULT_LEADERBOARD_BANNER_SRC);
  const [selectedScoreSection, setSelectedScoreSection] = useState("");

  useEffect(() => {
    setTopCount(normalizeDisplayRowCount(getDisplayRowsParam(searchParams), 20));
  }, [searchParams]);

  useEffect(() => {
    let active = true;

    async function loadDisplayData() {
      setLoading(true);
      setError("");

      try {
        const [matchValue, matchLeaderboardResult, stageLeaderboard, canonicalResult] = await Promise.all([
          getMatch(matchId),
          listMatchLeaderboards({ matchId }),
          listStageLeaderboards({ matchId }),
          listCompetitionResults(matchId),
        ]);

        if (!active) return;
        const legacyMatchRows = Array.isArray(matchLeaderboardResult) ? matchLeaderboardResult : [];
        const canonicalRows = normalizeCanonicalArcResultRows(canonicalResult);
        setMatch(matchValue || null);
        setMatchRows(legacyMatchRows.length > 0 ? sortByTimeAsc(legacyMatchRows, "totalScore") : canonicalRows);
        setStageRows(Array.isArray(stageLeaderboard) ? stageLeaderboard : []);
      } catch (err) {
        if (!active) return;
        setError(err.message || "Failed to load display leaderboard.");
        setMatch(null);
        setMatchRows([]);
        setStageRows([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadDisplayData();
    return () => {
      active = false;
    };
  }, [matchId]);

  useEffect(() => {
    let active = true;
    const targetBannerSrc = String(match?.leaderboardBannerUrl || "").trim();

    setReadyBannerSrc(DEFAULT_LEADERBOARD_BANNER_SRC);

    if (!targetBannerSrc) {
      return () => {
        active = false;
      };
    }

    preloadLeaderboardBanner(targetBannerSrc)
      .then((loadedSrc) => {
        if (active) {
          setReadyBannerSrc(loadedSrc);
        }
      })
      .catch(() => {
        if (active) {
          setReadyBannerSrc(DEFAULT_LEADERBOARD_BANNER_SRC);
        }
      });

    return () => {
      active = false;
    };
  }, [match?.leaderboardBannerUrl]);

  function handleTopCountChange(nextValue) {
    const normalizedValue = normalizeDisplayRowCount(nextValue, 20);
    setTopCount(normalizedValue);
    const nextParams = new URLSearchParams(searchParams);
    if (normalizedValue === 20) {
      nextParams.delete("rows");
    } else {
      nextParams.set("rows", String(normalizedValue));
    }
    nextParams.delete("top");
    setSearchParams(nextParams, { replace: true, state: location.state });
  }

  const stageItems = useMemo(
    () => buildStageItems(match, stageRows),
    [match, stageRows],
  );
  const completedMatchRows = useMemo(
    () => matchRows.filter(isLeaderboardRowComplete),
    [matchRows],
  );
  const completedStageRows = useMemo(
    () => stageRows.filter(isLeaderboardRowComplete),
    [stageRows],
  );
  const requiredStageCount = useMemo(() => {
    const fromMatch = Array.isArray(match?.stages) ? match.stages.length : 0;
    return fromMatch > 0 ? fromMatch : stageItems.length;
  }, [match, stageItems]);
  const stageProgressByKey = useMemo(() => {
    const map = new Map();
    for (const row of completedStageRows) {
      const key = leaderboardRowKey(row);
      const stageId = row?.stageId || row?.matchStageId;
      if (!key || !stageId) continue;
      if (!map.has(key)) {
        map.set(key, new Set());
      }
      map.get(key).add(stageId);
    }

    const countMap = new Map();
    for (const [key, set] of map.entries()) {
      countMap.set(key, set.size);
    }
    return countMap;
  }, [completedStageRows]);
  const rankedRows = useMemo(() => {
    if (requiredStageCount <= 0) {
      return sortByTimeAsc(completedMatchRows.filter(isRankEligibleRow), "totalScore");
    }

    return sortByTimeAsc(
      completedMatchRows.filter(
        (row) =>
          isRankEligibleRow(row) &&
          (row?.canonicalAggregate ||
            (stageProgressByKey.get(leaderboardRowKey(row)) || 0) >=
            requiredStageCount),
      ),
      "totalScore",
    );
  }, [completedMatchRows, requiredStageCount, stageProgressByKey]);
  const scoreSection = selectedScoreSection || "aggregate";
  const stageDisplayRows = useMemo(
    () => buildStageDisplayRows(completedStageRows, stageItems, scoreSection),
    [completedStageRows, scoreSection, stageItems],
  );
  const displayRows = scoreSection === "aggregate" ? rankedRows : stageDisplayRows;
  const leaderScore = displayRows.length > 0 ? formatNumber(displayRows[0]?.totalScore) : null;

  return (
    <section className="leaderboard-page leaderboard-page-booth">
      <Helmet>
        <title>{match?.matchName ? `${match.matchName} Fullscreen Leaderboard | Range Pocket Competition` : "Fullscreen Leaderboard | Range Pocket Competition"}</title>
      </Helmet>

      {loading && (
        <BoothDisplayStatus
          title="Loading leaderboard"
          message="Preparing the fullscreen display"
        />
      )}
      {!loading && error && (
        <BoothDisplayStatus
          title="Leaderboard unavailable"
          message={error}
          actions={(
            <>
            <button type="button" className="match-search-button" onClick={() => window.location.reload()}>
              Try Again
            </button>
            <Link to={`/matches/arc/${encodeURIComponent(matchId)}`} className="registration-auth-secondary leaderboard-inline-link">
              Back to Match
            </Link>
            </>
          )}
        />
      )}
      {!loading && !error && (
        <BoothLeaderboardDisplay
          match={match}
          bannerSrc={readyBannerSrc}
          rows={displayRows}
          topCount={topCount}
          onTopCountChange={handleTopCountChange}
          onExit={() => navigate(displayReturn(location, `/matches/arc/${encodeURIComponent(matchId)}`))}
          leaderScore={leaderScore}
          filterControls={(
            <><RelatedMatchSelector match={match} family="arc" surface="display" />
            <label className="booth-top-control">
              <span>Scores</span>
              <select value={scoreSection} onChange={(event) => setSelectedScoreSection(event.target.value)}>
                <option value="aggregate">Match Aggregate</option>
                {stageItems.map((stage, index) => (
                  <option key={stage.stageId} value={stage.stageId}>{stageDisplayName(stage, index)}</option>
                ))}
              </select>
            </label></>
          )}
          emptyMessage={scoreSection === "aggregate" ? stageItems.length ? "No completed match totals yet. Choose a stage in Scores to review its uploaded results." : "No completed match scores yet" : "No confirmed scores for this stage yet"}
        />
      )}
    </section>
  );
}

function LeaderboardMatchDetailRoute() {
  const { matchId } = useParams();
  // Stage selections, setup caches and in-flight detail reads belong to one match.
  return <LeaderboardMatchDetail key={matchId} />;
}

export const LeaderboardMatchDetail_Page = {
  path: "/matches/arc/:matchId",
  element: <LeaderboardMatchDetailRoute />,
};

export const LeaderboardMatchDisplay_Page = {
  path: "/matches/arc/:matchId/display",
  element: <LeaderboardMatchDisplay />,
};
